import React, { useRef, useState } from 'react';
import { PenLine, Type, Calendar, CheckSquare, User, Trash2, MousePointer } from 'lucide-react';

const FIELD_TYPES = [
  { type: 'signature', icon: PenLine, label: 'Signature', color: '#1d4ed8', bg: 'rgba(29,78,216,0.1)' },
  { type: 'initials', icon: PenLine, label: 'Initials', color: '#0891b2', bg: 'rgba(8,145,178,0.1)' },
  { type: 'text', icon: Type, label: 'Text', color: '#7c3aed', bg: 'rgba(124,58,237,0.1)' },
  { type: 'date', icon: Calendar, label: 'Date', color: '#059669', bg: 'rgba(5,150,105,0.1)' },
  { type: 'name', icon: User, label: 'Name', color: '#0f766e', bg: 'rgba(15,118,110,0.1)' },
  { type: 'checkbox', icon: CheckSquare, label: 'Checkbox', color: '#d97706', bg: 'rgba(217,119,6,0.1)' },
];

export default function HtmlFieldPlacer({ html, fields, onChange }) {
  const containerRef = useRef(null);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedField, setSelectedField] = useState(null);

  const handleContainerClick = (e) => {
    if (!selectedType || !containerRef.current) return;
    if (e.target.closest('[data-field-id]')) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    const defaults = {
      signature: { width: 22, height: 6 },
      initials: { width: 10, height: 5 },
      text: { width: 18, height: 4 },
      date: { width: 14, height: 4 },
      name: { width: 18, height: 4 },
      checkbox: { width: 4, height: 4 },
    };
    const size = defaults[selectedType] || { width: 18, height: 5 };

    const newField = {
      id: `field_${Date.now()}`,
      type: selectedType,
      page: 0,
      x, y,
      width: size.width,
      height: size.height,
      required: true,
      label: selectedType,
    };
    onChange([...fields, newField]);
    setSelectedType(null);
    setSelectedField(newField.id);
  };

  const removeField = (id) => {
    onChange(fields.filter(f => f.id !== id));
    if (selectedField === id) setSelectedField(null);
  };

  return (
    <div className="flex gap-4">
      <div className="w-44 flex-shrink-0 space-y-2">
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Signing Fields</p>
        {FIELD_TYPES.map(ft => {
          const Icon = ft.icon;
          return (
            <button
              key={ft.type}
              type="button"
              onClick={() => setSelectedType(selectedType === ft.type ? null : ft.type)}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-sm text-xs font-medium border transition-colors ${
                selectedType === ft.type
                  ? 'border-blue-400 bg-blue-50 text-blue-700'
                  : 'border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              <Icon size={13} /> {ft.label}
            </button>
          );
        })}
        {selectedType && (
          <p className="text-xs text-blue-600 flex items-center gap-1">
            <MousePointer size={11} /> Click on document to place
          </p>
        )}
        {selectedField && (
          <button
            type="button"
            onClick={() => removeField(selectedField)}
            className="w-full flex items-center justify-center gap-1 px-3 py-1.5 text-xs text-red-600 border border-red-200 rounded-sm hover:bg-red-50"
          >
            <Trash2 size={12} /> Remove field
          </button>
        )}
      </div>

      <div
        ref={containerRef}
        onClick={handleContainerClick}
        className={`flex-1 relative bg-white border rounded-sm shadow-sm min-h-[500px] overflow-auto ${
          selectedType ? 'cursor-crosshair border-blue-400' : 'border-slate-200'
        }`}
      >
        <div
          className="document-preview prose prose-sm max-w-none p-8"
          dangerouslySetInnerHTML={{ __html: html }}
        />
        {fields.map(field => {
          const ft = FIELD_TYPES.find(t => t.type === field.type) || FIELD_TYPES[0];
          return (
            <div
              key={field.id}
              data-field-id={field.id}
              onClick={(e) => { e.stopPropagation(); setSelectedField(field.id); }}
              style={{
                position: 'absolute',
                left: `${field.x}%`,
                top: `${field.y}%`,
                width: `${field.width}%`,
                height: `${field.height}%`,
                transform: 'translate(-50%, -50%)',
                border: `2px dashed ${ft.color}`,
                background: ft.bg,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 10,
                outline: selectedField === field.id ? `2px solid ${ft.color}` : 'none',
              }}
            >
              <span style={{ fontSize: 10, color: ft.color, fontWeight: 600 }}>{ft.label} *</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
